// BUILD-046 Owner-requested polish (visual edits ledger, session edit 4.1): the short, muted REQUESTER BADGE that replaces the long "Requested by <name> - Manual"
// text of a task row:  "By Julie · Oct 18".
//
// Presentation only. It writes two RECORDED facts and invents nothing:
//   - the requester's FIRST NAME, the first word of the recorded requester name ("Julie Manager (FICTIONAL)" gives "Julie"). Its letters are never re-cased.
//     D1 display cleanup: the badge SHOWS the short form of the name (displayNames.personDisplay: first name + last initial, "By Alex S."; a role word such as
//     Manager is never a surname, so "Julie Manager" still gives "By Julie") and the tooltip and accessible name carry the full name WITHOUT the "(FICTIONAL)" marker.
//   - the REQUEST date, the task's recorded creation date (`createdAt`), written as a short calendar date in PHOENIX time ("Oct 18"; "Oct 18, 2025" when the year is
//     not the current Phoenix year). `updatedAt` is never read: a task touched later does not look newly requested. A date-only string is taken as the date it
//     says (never parsed with `new Date(string)`, which is UTC midnight and the previous day in Phoenix); an instant is converted to its Phoenix calendar day;
//     anything else (missing, empty, not a date, a date-time with no zone) leaves the date out and the badge reads "By Julie".
// No recorded requester name: no badge (the caller shows nothing). WHERE the badge is shown is the caller's existing rule for the requester text of its view.
//
// The full name and the full date go in the tooltip and the accessible name. For a manual task (no Source_Reference, not source-governed) the tooltip also says
// "Manual task", which is where the old visible "Manual" went; a source-linked task keeps its quiet visible "From <module>" (OriginChip).
//
// Pure module. Runtime imports: pure date helpers and the origin rule. Erasable TypeScript only.

import { isIsoDate, phoenixToday } from './dateLabels';
import { shortDate } from './dueLabel';
import { originLabel } from './originModel';
import type { OriginInput } from './originModel';
import { personDisplay } from './displayNames';

export interface RequesterBadgeInput {
  requesterName?: unknown;
  createdAt?: unknown;
}

export interface RequesterBadgeOptions {
  /** The instant that decides "the current Phoenix year" (default: now). */
  now?: Date;
  /** True for a manual task: the tooltip then says "Manual task". */
  manual?: boolean;
}

export interface RequesterBadge {
  /** The recorded requester name as it is SHOWN in full: the "(FICTIONAL)" test marker removed, nothing else changed (displayNames.personDisplay). */
  fullName: string;
  /** Its first word. */
  firstName: string;
  /** What the badge shows for the person: the first name plus the last initial ("Alex S."), or the first name alone when the name has no surname or ends in a role word. */
  shortName: string;
  /** The request date as a Phoenix `YYYY-MM-DD`, or null when the record has no usable date. */
  requestDate: string | null;
  /** `Oct 18` / `Oct 18, 2025`, or null. */
  shortDate: string | null;
  /** `October 18, 2026`, or null. */
  longDate: string | null;
  /** What the badge shows: `By Julie · Oct 18`, or `By Julie` without a date. */
  text: string;
  /** The hover and focus tooltip: `Requested by Julie Manager (FICTIONAL) on October 18, 2026`, plus ` · Manual task` for a manual task. */
  tooltip: string;
  /** The same tooltip as the lines it is drawn in: the request sentence, then `Manual task` for a manual task. */
  tooltipLines: string[];
  /** The badge's complete accessible name (the same facts as the tooltip, as sentences). */
  ariaLabel: string;
}

const MONTHS_LONG: readonly string[] = Object.freeze(['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']);

// A date-time must carry its zone (Z or an offset): without one the instant is ambiguous and is not used.
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** The first word of a recorded name, or null when the name is missing or blank. Never re-cased. */
export function firstNameOf(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const first = name.trim().split(/\s+/)[0];
  return first === undefined || first === '' ? null : first;
}

/**
 * The Phoenix calendar day (`YYYY-MM-DD`) a recorded creation value falls on, or null when it is not a usable date. A date-only value is that very date; an
 * instant (with its zone) is converted to Phoenix time, so 2026-10-19T03:30:00Z is October 18 in Phoenix, not October 19.
 */
export function requestDateOf(createdAt: unknown): string | null {
  if (typeof createdAt !== 'string') return null;
  const text = createdAt.trim();
  if (isIsoDate(text)) return text;
  if (!INSTANT.test(text)) return null;
  const ms = Date.parse(text);
  if (Number.isNaN(ms)) return null;
  const day = phoenixToday(new Date(ms));
  return isIsoDate(day) ? day : null;
}

/** `2026-10-18` is `October 18, 2026`. Not a real date: ''. */
export function longDateOf(iso: unknown): string {
  if (!isIsoDate(iso)) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return `${MONTHS_LONG[m - 1]} ${d}, ${y}`;
}

/** The badge for a task, or null when the record has no requester name. Reads `requesterName` and `createdAt` only. */
export function describeRequesterBadge(input: RequesterBadgeInput, options: RequesterBadgeOptions = {}): RequesterBadge | null {
  const firstName = firstNameOf(input.requesterName);
  if (firstName === null) return null;
  // The badge shows the SHORT form (first name + last initial; a role word such as Manager is never a surname) and the tooltip the full name, both without the test marker.
  const person = personDisplay(input.requesterName as string);
  const fullName = person.full !== '' ? person.full : (input.requesterName as string).trim();
  const shortName = person.short !== '' ? person.short : firstName;
  const requestDate = requestDateOf(input.createdAt);
  const today = phoenixToday(options.now ?? new Date());
  const short = requestDate === null ? null : shortDate(requestDate, today);
  const long = requestDate === null ? null : longDateOf(requestDate);
  const manual = options.manual === true;
  const tooltipLines = [`Requested by ${fullName}${long !== null && long !== '' ? ` on ${long}` : ''}`, ...(manual ? ['Manual task'] : [])];
  const tooltip = tooltipLines.join(' \u00b7 ');
  const ariaLabel = `Requested by ${fullName}${long !== null && long !== '' ? ` on ${long}` : ''}.${manual ? ' Manual task.' : ''}`;
  return {
    fullName,
    firstName,
    shortName,
    requestDate,
    shortDate: short !== null && short !== '' ? short : null,
    longDate: long !== null && long !== '' ? long : null,
    text: `By ${shortName}${short !== null && short !== '' ? ` \u00b7 ${short}` : ''}`,
    tooltip,
    tooltipLines,
    ariaLabel,
  };
}

/** A task as the rows hold it: the badge's two recorded facts plus what decides whether it is manual. */
export interface RequesterBadgeTask extends RequesterBadgeInput, OriginInput {}

/**
 * The badge of a task row. `show` is the CALLER's existing rule for showing the requester text in its view (My Tasks: a recorded requester who is not the Caller;
 * Shared Tasks, Team and Company: any recorded requester); when it is false there is no badge.
 */
export function badgeForTask(task: RequesterBadgeTask, show: boolean, now?: Date): RequesterBadge | null {
  if (!show) return null;
  const manual = originLabel(task).kind === 'manual';
  return describeRequesterBadge(task, { manual, now });
}
